import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Bell, CalendarPlus } from 'lucide-react';
import { TeamMeditation } from '@/lib/hooks/use-team-meditations';
import { downloadIcs } from '@/lib/utils';

interface MeditationCountdownProps {
  meditation: TeamMeditation | null;
  activeMeditation: TeamMeditation | null;
  onSwitchToTab?: () => void;
  variant?: 'team' | 'samen';
}

export function MeditationCountdown({
  meditation,
  activeMeditation,
  variant = 'team',
  onSwitchToTab,
}: MeditationCountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    if (!meditation) return;

    const startTime = new Date(meditation.scheduled_at);

    const calculateTimeRemaining = () => {
      const now = new Date();
      if (now >= startTime) {
        setTimeRemaining(null);
        return;
      }

      const totalSeconds = Math.floor(
        (startTime.getTime() - now.getTime()) / 1000,
      );

      setTimeRemaining({
        days: Math.floor(totalSeconds / (60 * 60 * 24)),
        hours: Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60)),
        minutes: Math.floor((totalSeconds % (60 * 60)) / 60),
        seconds: Math.floor(totalSeconds % 60),
      });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [meditation?.scheduled_at]);

  // Show active meditation banner
  if (activeMeditation) {
    return (
      <Card className="bg-green-50 border-2 border-green-500 shadow-lg overflow-hidden mb-6 animate-in fade-in duration-500">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Bell className="h-6 w-6 text-green-600 animate-bounce" />
              </div>
              <div>
                <p className="font-semibold text-green-800">
                  De team meditatie is nu bezig!
                </p>
                <p className="text-sm text-green-600">
                  {activeMeditation.meditation_title}
                </p>
              </div>
            </div>
            {onSwitchToTab && (
              <button
                onClick={onSwitchToTab}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                Doe mee
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!meditation || !timeRemaining) return null;

  const scheduledDate = new Date(meditation.scheduled_at);
  const isExercise = (meditation as any).activity_type === 'exercise';
  const groupLabel = variant === 'samen' ? 'Samen' : 'Team';
  const countdownLabel = isExercise ? `Volgende ${groupLabel} Oefening` : `Volgende ${groupLabel} Meditatie`;

  return (
    <Card className="bg-white shadow-md border-green-500 overflow-hidden mb-6">
      <CardHeader className="bg-gradient-to-r from-green-500/10 to-blue-500/10 pb-2">
        <CardTitle className="flex items-center text-lg font-medium text-green-800">
          <Clock className="h-5 w-5 mr-2 text-green-600" />
          {countdownLabel}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-muted-foreground">
              {meditation.meditation_title}
            </span>
            <span className="text-sm text-muted-foreground">
              {scheduledDate.toLocaleDateString('nl-NL', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}{' '}
              om{' '}
              {scheduledDate.toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>

          <div className="grid grid-cols-4 gap-2 mt-2">
            <div className="flex flex-col items-center">
              <div className="text-2xl font-bold text-green-600">
                {timeRemaining.days}
              </div>
              <div className="text-xs text-muted-foreground">Dagen</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-2xl font-bold text-green-600">
                {timeRemaining.hours}
              </div>
              <div className="text-xs text-muted-foreground">Uren</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-2xl font-bold text-green-600">
                {timeRemaining.minutes}
              </div>
              <div className="text-xs text-muted-foreground">Min</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-2xl font-bold text-green-600">
                {timeRemaining.seconds}
              </div>
              <div className="text-xs text-muted-foreground">Sec</div>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full text-green-700 border-green-300 hover:bg-green-50"
            onClick={() =>
              downloadIcs(
                meditation.meditation_title,
                meditation.scheduled_at,
                meditation.duration_minutes ?? 20,
                { variant, activityType: (meditation as any).activity_type },
              )
            }
          >
            <CalendarPlus className="h-4 w-4 mr-2" />
            Voeg toe aan agenda
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
